import type { UseMutationResult } from 'react-query';
import { useMutation, useQueryClient } from 'react-query';

import type { IApiGetUserResponseBody } from '../../Users';
import { useApi } from '~/lib/api/useApi';
import { userQueryKeyFactory } from '../../Users/hooks/useUserQuery';
import { organizationQueryKeyFactory } from './useOrganizationQuery';
import { organizationsQueryKeyFactory } from './useOrganizationsQuery';
import { organizationUsersQueryKeyFactory } from './useOrganizationUsersQuery';

export type IUseOrganizationRemoveUserMutationValues = {
  organizationId: string;
  userId: string;
};

export const useOrganizationRemoveUserMutation = (): UseMutationResult<
  IApiGetUserResponseBody,
  unknown,
  IUseOrganizationRemoveUserMutationValues
> => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values) =>
      api.organizations.removeUser(values.organizationId, values.userId),
    onSuccess: async (updatedUser, values) => {
      await Promise.all(
        [
          {
            queryKey: organizationUsersQueryKeyFactory({
              organizationId: values.organizationId,
            }),
          },
          {
            queryKey: organizationQueryKeyFactory({
              organizationId: values.organizationId,
            }),
          },
          { queryKey: organizationsQueryKeyFactory() },
        ].map((filters) => queryClient.invalidateQueries(filters)),
      );

      queryClient.setQueryData(
        userQueryKeyFactory({ userId: updatedUser._id })!,
        updatedUser,
      );
    },
  });
};
